/**
 * `ChipAutocomplete` — campo multi-selezione a "chip" con autocomplete.
 *
 * Sostituisce le liste di checkbox nelle associazioni N:N (es. skill↔competenze):
 * le voci selezionate sono mostrate come chip rimovibili, e un input di testo
 * filtra le opzioni residue mostrandole in un menu a discesa.
 *
 * Il valore è gestito dall'esterno come array di `id` (`selectedIds`), coerente
 * con il modello dati (`competenceIds` / `skillIds`). Il componente non muta lo
 * stato: notifica aggiunte/rimozioni via `onChange`.
 *
 * Accessibilità: pattern combobox (`role="combobox"` + `listbox`/`option`),
 * navigazione con frecce/Invio/Esc, chip rimovibili con etichetta accessibile.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';

/** Opzione selezionabile: `id` stabile + etichetta visibile. */
export interface ChipOption {
  id: string;
  name: string;
}

interface ChipAutocompleteProps {
  /** `id` del controllo, collegato alla `<label>` del `Field`. */
  id: string;
  /** Elenco completo delle opzioni disponibili. */
  options: readonly ChipOption[];
  /** `id` attualmente selezionati. */
  selectedIds: readonly string[];
  /** Notifica il nuovo elenco di `id` selezionati. */
  onChange: (next: string[]) => void;
  /** Placeholder dell'input di ricerca. */
  placeholder?: string;
  /** Messaggio quando non ci sono corrispondenze. */
  emptyMessage?: string;
}

/** Normalizza per il confronto case-insensitive/accento-tollerante minimale. */
function normalize(value: string): string {
  return value.trim().toLowerCase();
}

export function ChipAutocomplete({
  id,
  options,
  selectedIds,
  onChange,
  placeholder = 'Cerca e seleziona…',
  emptyMessage = 'Nessuna corrispondenza.',
}: ChipAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // Posizione del menu, calcolata rispetto alla viewport. Il menu è reso in
  // `position: fixed` (non `absolute`) così da NON essere clippato da antenati
  // con `overflow` (es. il corpo scrollabile di un modale). `openUp` apre verso
  // l'alto quando sotto il controllo non c'è spazio sufficiente.
  const [menuStyle, setMenuStyle] = useState<{
    left: number;
    top: number;
    width: number;
    openUp: boolean;
  } | null>(null);

  // Mappa id→opzione per risolvere i chip selezionati in ordine di selezione.
  const optionById = useMemo(() => {
    const map = new Map<string, ChipOption>();
    for (const o of options) map.set(o.id, o);
    return map;
  }, [options]);

  const selected = useMemo(
    () => selectedIds.map((sid) => optionById.get(sid)).filter((o): o is ChipOption => !!o),
    [selectedIds, optionById],
  );

  // Opzioni non ancora selezionate che matchano la query.
  const suggestions = useMemo(() => {
    const q = normalize(query);
    const selectedSet = new Set(selectedIds);
    return options
      .filter((o) => !selectedSet.has(o.id))
      .filter((o) => (q === '' ? true : normalize(o.name).includes(q)));
  }, [options, selectedIds, query]);

  // Mantiene l'indice attivo entro i limiti quando cambiano i suggerimenti.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, suggestions.length - 1)));
  }, [suggestions.length]);

  // Chiude il menu al click esterno.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Calcola la posizione del menu (fixed) sotto/sopra il controllo e la tiene
  // allineata su scroll e resize finché il menu è aperto. Lo scroll è
  // intercettato in capture per reagire anche allo scroll di antenati interni
  // (es. il corpo del modale), non solo a quello della finestra.
  useEffect(() => {
    if (!open) return;

    const reposition = () => {
      const control = controlRef.current;
      if (!control) return;
      const rect = control.getBoundingClientRect();
      const gap = 4;
      const menuMaxHeight = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      // Apre verso l'alto solo se sotto non basta lo spazio ma sopra sì.
      const openUp = spaceBelow < menuMaxHeight + gap && rect.top > spaceBelow;
      setMenuStyle({
        left: rect.left,
        top: openUp ? rect.top - gap : rect.bottom + gap,
        width: rect.width,
        openUp,
      });
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, selectedIds.length, suggestions.length]);

  const add = (optionId: string) => {
    if (selectedIds.includes(optionId)) return;
    onChange([...selectedIds, optionId]);
    setQuery('');
    setActiveIndex(0);
    // Mantiene il focus sull'input per selezioni consecutive.
    inputRef.current?.focus();
  };

  const remove = (optionId: string) => {
    onChange(selectedIds.filter((sid) => sid !== optionId));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setOpen(true);
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (open && suggestions[activeIndex]) {
          e.preventDefault();
          add(suggestions[activeIndex].id);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
      case 'Backspace':
        // Con input vuoto, rimuove l'ultimo chip selezionato.
        if (query === '' && selected.length > 0) {
          e.preventDefault();
          remove(selected[selected.length - 1].id);
        }
        break;
      default:
        break;
    }
  };

  const activeOptionId =
    open && suggestions[activeIndex] ? `${listboxId}-opt-${suggestions[activeIndex].id}` : undefined;

  return (
    <div className="chip-autocomplete" ref={rootRef}>
      <div
        className="chip-autocomplete__control"
        ref={controlRef}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selected.map((o) => (
          <span key={o.id} className="chip-autocomplete__chip">
            <span className="chip-autocomplete__chip-label">{o.name}</span>
            <button
              type="button"
              className="chip-autocomplete__chip-remove"
              aria-label={`Rimuovi ${o.name}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(o.id);
              }}
            >
              <Icon name="close" size={12} />
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          className="chip-autocomplete__input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          autoComplete="off"
          value={query}
          placeholder={selected.length === 0 ? placeholder : ''}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {open && menuStyle && (
        <ul
          className="chip-autocomplete__menu"
          id={listboxId}
          role="listbox"
          style={{
            position: 'fixed',
            left: menuStyle.left,
            width: menuStyle.width,
            // Aperto verso l'alto: ancoriamo il bordo inferiore del menu al top
            // del controllo (via `bottom`), così cresce verso l'alto.
            ...(menuStyle.openUp
              ? { bottom: window.innerHeight - menuStyle.top }
              : { top: menuStyle.top }),
          }}
        >
          {suggestions.length === 0 ? (
            <li className="chip-autocomplete__empty" role="presentation">
              {emptyMessage}
            </li>
          ) : (
            suggestions.map((o, index) => (
              <li
                key={o.id}
                id={`${listboxId}-opt-${o.id}`}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex
                    ? 'chip-autocomplete__option chip-autocomplete__option--active'
                    : 'chip-autocomplete__option'
                }
                onMouseDown={(e) => {
                  // `mousedown` per evitare il blur dell'input prima del click.
                  e.preventDefault();
                  add(o.id);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {o.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export default ChipAutocomplete;
